import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deleteNotificationById,
  getNotificationsByUser,
} from "../../../../services/notificationServices";

import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  ADMIN_NOTIFICATION_USER_ID,
  DEFAULT_FILTERS,
  buildNotificationStats,
  enrichNotifications,
  filterNotifications,
  paginateItems,
  sortNotifications,
} from "../utils/notificationListUtils";

export function useAdminNotifications() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchNotifications = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getNotificationsByUser(ADMIN_NOTIFICATION_USER_ID);
      const safeData = Array.isArray(data) ? data : [];

      setNotifications(enrichNotifications(safeData));

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Notifications actualisées.", "success");
      }
    } catch (error) {
      console.error("Erreur récupération notifications :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des notifications.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications({ silent: true });
  }, [fetchNotifications]);

  const filteredNotifications = useMemo(() => {
    const filtered = filterNotifications(notifications, filters);
    return sortNotifications(filtered, filters.sort);
  }, [filters, notifications]);

  const pagination = useMemo(() => {
    return paginateItems(filteredNotifications, page, filters.perPage);
  }, [filteredNotifications, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildNotificationStats(notifications);
  }, [notifications]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const handleDeleteNotification = useCallback(async (notification) => {
    if (!notification?.id) return;

    const confirmDelete = window.confirm("Supprimer cette notification ?");

    if (!confirmDelete) return;

    setDeletingId(notification.id);

    try {
      await deleteNotificationById(notification.id);

      setNotifications((prev) =>
        prev.filter((item) => item.id !== notification.id),
      );

      notificationRef.current?.(
        "Notification supprimée avec succès.",
        "success",
      );
    } catch (error) {
      console.error("Erreur suppression notification :", error);

      notificationRef.current?.(
        "Erreur lors de la suppression de la notification.",
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  }, []);

  return {
    notifications,
    filteredNotifications,
    paginatedNotifications: pagination.items,
    filters,
    stats,
    loading,
    refreshing,
    deletingId,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchNotifications,
    updateFilter,
    resetFilters,
    setPage,
    handleDeleteNotification,
  };
}